/**
 * ChecksumBadges — compact MD5 / SHA1 display for uploaded files.
 *
 *  • Shows monospace badges with copy-to-clipboard for MD5 and SHA1.
 *  • Status pill ("calculating…" / "—") for files where MD5 isn't ready yet.
 *  • SHA1 from S3 multipart uploads is in `<hex>-<N>` form; we surface it
 *    verbatim so audits match the AWS console value.
 *  • If the customer also uploaded a `.md5` sidecar, we render a verification
 *    badge — green "MD5 verified" / red "MD5 mismatch" / amber "Awaiting
 *    match" — so a single glance tells the operator whether the customer's
 *    declared hash matches OwnTune's computed hash.
 */
import { Copy, Check, ShieldCheck, ShieldX, ShieldAlert, FileCheck2, AlertTriangle } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

/**
 * Truncates a hex hash for compact display while keeping enough characters
 * to be visually identifiable (first 8, last 6). Full value is still copied.
 */
const shortHash = (s) => {
    if (!s) return '';
    if (s.length <= 18) return s;
    return `${s.slice(0, 8)}…${s.slice(-6)}`;
};

function CopyButton({ value, label }) {
    const [copied, setCopied] = useState(false);
    const onClick = async (e) => {
        e.stopPropagation();
        e.preventDefault();
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            toast.success(`${label} copied`);
            setTimeout(() => setCopied(false), 1400);
        } catch {
            toast.error('Copy failed');
        }
    };
    return (
        <button
            type="button"
            onClick={onClick}
            className="opacity-70 hover:opacity-100 text-slate-500 hover:text-slate-900 transition"
            title={`Copy ${label}`}
            data-testid={`copy-${label.toLowerCase()}`}
        >
            {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
        </button>
    );
}

/**
 * Renders the verification verdict for a MASTER file whose hash was checked
 * against a customer-supplied `.md5` sidecar.
 */
function VerificationBadge({ verdict, declared }) {
    if (!verdict) return null;
    const config = {
        match: {
            className: 'bg-emerald-50 border-emerald-200 text-emerald-700',
            Icon: ShieldCheck,
            label: 'MD5 verified',
            testid: 'md5-verified-match',
        },
        mismatch: {
            className: 'bg-rose-50 border-rose-200 text-rose-700',
            Icon: ShieldX,
            label: 'MD5 mismatch',
            testid: 'md5-verified-mismatch',
        },
        pending: {
            className: 'bg-amber-50 border-amber-200 text-amber-700',
            Icon: ShieldAlert,
            label: 'Awaiting hash',
            testid: 'md5-verified-pending',
        },
    }[verdict];
    if (!config) return null;
    const { className, Icon, label, testid } = config;
    return (
        <span
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-medium ${className}`}
            title={declared ? `Declared MD5: ${declared}` : label}
            data-testid={testid}
        >
            <Icon className="h-3 w-3" />
            {label}
        </span>
    );
}

/**
 * Renders the overall verdict for a `.md5` sidecar file: how many of its
 * declared entries matched / mismatched / are still pending a sibling upload.
 */
function ChecksumFileSummary({ summary, entries }) {
    if (!summary) return null;
    const totals = (entries || []).reduce(
        (acc, e) => {
            acc.total += 1;
            acc[e.verdict || 'pending'] = (acc[e.verdict || 'pending'] || 0) + 1;
            return acc;
        },
        { total: 0, match: 0, mismatch: 0, pending: 0 },
    );
    const config = {
        match: {
            className: 'bg-emerald-50 border-emerald-200 text-emerald-700',
            label: `Checksum manifest verified (${totals.match}/${totals.total})`,
            testid: 'checksum-file-match',
        },
        partial: {
            className: 'bg-emerald-50 border-emerald-200 text-emerald-700',
            label: `Partially verified (${totals.match}/${totals.total})`,
            testid: 'checksum-file-partial',
        },
        mismatch: {
            className: 'bg-rose-50 border-rose-200 text-rose-700',
            label: `Checksum mismatch (${totals.mismatch}/${totals.total})`,
            testid: 'checksum-file-mismatch',
        },
        pending: {
            className: 'bg-amber-50 border-amber-200 text-amber-700',
            label: `Awaiting matching uploads (${totals.pending}/${totals.total})`,
            testid: 'checksum-file-pending',
        },
        empty: {
            className: 'bg-slate-50 border-slate-200 text-slate-500',
            label: 'No MD5 entries found',
            testid: 'checksum-file-empty',
        },
    }[summary];
    if (!config) return null;
    const { className, label, testid } = config;
    return (
        <div className="mt-1 space-y-1" data-testid="checksum-file-summary">
            <span
                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-medium ${className}`}
                data-testid={testid}
            >
                <FileCheck2 className="h-3 w-3" />
                {label}
            </span>
            {(entries || []).length > 0 && (
                <ul className="text-[10px] text-slate-500 font-mono space-y-0.5 pl-1">
                    {entries.map((entry, idx) => {
                        const dot = {
                            match: 'bg-emerald-500',
                            mismatch: 'bg-rose-500',
                            pending: 'bg-amber-400',
                        }[entry.verdict || 'pending'];
                        return (
                            <li
                                key={`${entry.md5}-${idx}`}
                                className="flex items-center gap-1.5"
                                data-testid={`checksum-entry-${entry.verdict || 'pending'}`}
                            >
                                <span className={`inline-block h-1.5 w-1.5 rounded-full ${dot}`} />
                                <span className="truncate max-w-[180px]" title={entry.filename || ''}>
                                    {entry.filename || '(unspecified target)'}
                                </span>
                                <span className="text-slate-400">{shortHash(entry.md5)}</span>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}

/**
 * Non-blocking warning when this master file has the same MD5/SHA1 as
 * another large master file uploaded to a DIFFERENT disc/layer. Hint to the
 * customer / admin that the same content may have been mistakenly attached
 * to two different discs.
 */
function DuplicateMasterWarning({ matches }) {
    if (!matches || matches.length === 0) return null;
    return (
        <div
            className="mt-1 inline-flex items-start gap-1.5 px-2 py-1 rounded border border-amber-200 bg-amber-50 text-amber-800 text-[11px] leading-tight"
            data-testid="duplicate-master-warning"
        >
            <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-px text-amber-600" />
            <div className="font-sans">
                <span className="font-medium">Possible duplicate master file: </span>
                this file appears to be identical to{' '}
                {matches.length === 1 ? (
                    <>
                        the file uploaded for{' '}
                        <span className="font-semibold">{matches[0].disc_label}</span>
                        {matches[0].filename && (
                            <> (<span className="font-mono">{matches[0].filename}</span>)</>
                        )}
                        .
                    </>
                ) : (
                    <>
                        {' '}
                        files uploaded for{' '}
                        {matches.map((m, i) => (
                            <span key={m.file_id}>
                                {i > 0 && (i === matches.length - 1 ? ' and ' : ', ')}
                                <span className="font-semibold">{m.disc_label}</span>
                            </span>
                        ))}
                        . Please confirm this is intentional.
                    </>
                )}
            </div>
        </div>
    );
}

/**
 * Render checksums for one file.
 * Props:
 *   - file:      the full file record (preferred — auto-derives the rest)
 *   - md5:       full MD5 hex string (or null) — used when `file` is absent
 *   - sha1:      full SHA1 string from S3 (or null) — used when `file` is absent
 *   - status:    "calculating" | "ready" | "failed" | undefined
 *   - verification: optional { verdict, declared } when the master was checked
 *                   against a customer-uploaded .md5 file.
 *   - checksumFile: optional { summary, entries } when the file IS a .md5
 *                   sidecar — renders the manifest's overall verdict.
 */
export function ChecksumBadges({ file, md5, sha1, status, verification, checksumFile, duplicateMatches }) {
    // When the caller passes the raw file record, derive everything from it.
    if (file) {
        md5 = md5 ?? file.md5_checksum ?? null;
        sha1 = sha1 ?? file.sha1_checksum ?? null;
        if (!verification && file.md5_verified) {
            verification = {
                verdict: file.md5_verified,
                declared: file.md5_verified_against_md5,
            };
        }
        if (!checksumFile && file.is_checksum_file) {
            checksumFile = {
                summary: file.checksum_summary,
                entries: file.checksum_entries || [],
            };
        }
        if (!duplicateMatches && Array.isArray(file.duplicate_master_matches) && file.duplicate_master_matches.length > 0) {
            duplicateMatches = file.duplicate_master_matches;
        }
    }

    const effectiveStatus =
        status ||
        (md5 || sha1 ? 'ready' : undefined);

    if (effectiveStatus === 'calculating') {
        return (
            <div className="flex items-center gap-1 text-[10px] text-slate-400" data-testid="checksum-calculating">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-slate-300 animate-pulse" />
                Calculating checksums…
            </div>
        );
    }

    if (effectiveStatus === 'failed' && !md5 && !sha1) {
        return (
            <div className="text-[10px] text-rose-500" data-testid="checksum-failed">
                Checksum failed
            </div>
        );
    }

    if (!md5 && !sha1 && !verification && !checksumFile && (!duplicateMatches || duplicateMatches.length === 0)) return null;

    return (
        <div className="flex flex-col gap-1" data-testid="checksum-badges">
            <div className="flex flex-wrap items-center gap-2 text-[10px] font-mono">
                {md5 && (
                    <span
                        className="inline-flex items-center gap-1 bg-slate-100 border border-slate-200 text-slate-700 px-1.5 py-0.5 rounded"
                        title={`MD5: ${md5}`}
                    >
                        <span className="text-slate-400 font-sans">MD5</span>
                        <span>{shortHash(md5)}</span>
                        <CopyButton value={md5} label="MD5" />
                    </span>
                )}
                {sha1 && (
                    <span
                        className="inline-flex items-center gap-1 bg-slate-100 border border-slate-200 text-slate-700 px-1.5 py-0.5 rounded"
                        title={`SHA1 (S3): ${sha1}`}
                    >
                        <span className="text-slate-400 font-sans">SHA1</span>
                        <span>{shortHash(sha1)}</span>
                        <CopyButton value={sha1} label="SHA1" />
                    </span>
                )}
                {verification && (
                    <VerificationBadge
                        verdict={verification.verdict}
                        declared={verification.declared}
                    />
                )}
                {file?.is_system_md5_sidecar && (
                    <span
                        data-testid="flow-md5-generated-badge"
                        className="inline-flex items-center gap-1 bg-emerald-50 border border-emerald-200 text-emerald-700 px-1.5 py-0.5 rounded font-sans"
                        title={`FLOW generated this .md5 sidecar from the server-computed hash of ${file.source_filename || 'the source master'}.`}
                    >
                        <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" />
                        MD5 generated by FLOW
                    </span>
                )}
                {!file?.is_system_md5_sidecar && file?.md5_verified_by_flow_sidecar && (
                    <span
                        data-testid="flow-md5-source-badge"
                        className="inline-flex items-center gap-1 bg-emerald-50 border border-emerald-200 text-emerald-700 px-1.5 py-0.5 rounded font-sans"
                        title="FLOW generated a .md5 sidecar for this master from its trusted server-computed hash."
                    >
                        <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" />
                        MD5 generated by FLOW
                    </span>
                )}
            </div>
            {checksumFile && (
                <ChecksumFileSummary
                    summary={checksumFile.summary}
                    entries={checksumFile.entries}
                />
            )}
            {duplicateMatches && duplicateMatches.length > 0 && (
                <DuplicateMasterWarning matches={duplicateMatches} />
            )}
        </div>
    );
}

export default ChecksumBadges;
